import React from 'react';
import PropTypes from 'prop-types';

import { Trans, useTranslation } from 'react-i18next';
import Styles from '../../../styles/minisite/minisite.module.css';
import { Input, ToggleLabel, Form, TooltipHelp, Button, FieldErrors } from '@weezevent/nacre';
import ConfirmModal from '../../../components/confirmModal';
import { Link } from 'react-router-dom';

export default function SecurityOptions({ initialValues, formMinisite, handleChange, resetValue, submitSuccess, isLoading }) {
    const { t } = useTranslation();

    const [showPassword, setShowPassword] = React.useState(formMinisite['password_protection']);
    const [modalConfirm, setModalConfirm] = React.useState(null);
    const toggleShowPassword = React.useCallback(
        (ev, { checked }) => {
            setShowPassword(!showPassword);
            if (!checked) {
                handleChange('password', '');
            }
        },
        [showPassword]
    );

    const [showMinisite, setShowMinisite] = React.useState(formMinisite['enable_seo_index']);

    // Close modal only if submitSuccess is true and loading is false
    React.useEffect(() => {
        if (isLoading || !submitSuccess) {
            return;
        }

        return setModalConfirm(null);
    }, [isLoading, submitSuccess]);

    const toggleShowMinisite = React.useCallback(
        (ev, { checked }) => {
            handleChange('enable_seo_index', checked);
            setShowMinisite(!showMinisite);
        },
        [handleChange, showMinisite]
    );

    const [errors, setErrors] = React.useState([]);

    const handleInputChange = React.useCallback(
        (value, update = true) => {
            handleChange('slug', value, update);
            const forbiddenCharactersRule = new RegExp(/[_:/\s#]/);
            // set errors if there's : / _ # or space in the value
            if (value.match(forbiddenCharactersRule)) {
                setErrors([t('ticket.event.minisite.security_options.slug_error')]);
            } else {
                setErrors([]);
            }
        },
        [handleChange, setErrors]
    );

    const handleInputPasswordChange = React.useCallback(
        (value, update = true) => {
            handleChange('password', value, update);
        },
        [handleChange]
    );

    return (
        <Form>
            <div className={Styles['options-subtitle']}>
                <Trans i18nKey="ticket.event.minisite.security_options.title" />
            </div>
            <Button fluid onClick={() => setModalConfirm('slug')}>
                <Trans i18nKey="ticket.event.minisite.security_options.edit_url" />
            </Button>
            <ToggleLabel
                inlinePosition="left"
                label={
                    <div className={Styles['options-title-toggle']}>
                        <Trans i18nKey="ticket.event.minisite.security_options.seo_title" />
                        <TooltipHelp mini triggerOnHover title={<Trans i18nKey="ticket.event.minisite.security_options.seo" />} />
                    </div>
                }
                onChange={toggleShowMinisite}
                value={formMinisite['enable_seo_index']}
                checked={showMinisite}
            />

            <ToggleLabel
                inlinePosition="left"
                label={
                    <div className={Styles['options-title-toggle']}>
                        <Trans i18nKey="ticket.event.minisite.security_options.protect_title" />
                        <TooltipHelp mini triggerOnHover title={<Trans i18nKey="ticket.event.minisite.security_options.protect" />} />
                    </div>
                }
                onChange={toggleShowPassword}
                checked={showPassword}
                value={formMinisite['password_protection']}
            />
            {showPassword && (
                <Form.Field>
                    <div className={Styles['options-input']}>
                        <Input
                            showSizes
                            maxLength={50}
                            placeholder={t('ticket.event.minisite.security_options.password_placeholder')}
                            type="text"
                            value={formMinisite['password']}
                            onChange={(ev, { value }) => handleInputPasswordChange(value, false)}
                        />
                        {initialValues['password'] !== formMinisite['password'] && (
                            <div className={Styles['option-save']}>
                                <Link
                                    className={Styles['option-reset']}
                                    to="#"
                                    onClick={event => {
                                        event.preventDefault();
                                        handleChange('password', initialValues['password'], false);
                                    }}
                                >
                                    {t('ticket.common.action.cancel')}
                                </Link>
                                <Link
                                    to="#"
                                    onClick={event => {
                                        event.preventDefault();
                                        handleInputPasswordChange(formMinisite['password']);
                                    }}
                                >
                                    {t('ticket.common.action.save')}
                                </Link>
                            </div>
                        )}
                    </div>
                </Form.Field>
            )}
            {modalConfirm && (
                <ConfirmModal
                    labelTitle={t(`ticket.event.minisite.${modalConfirm}.title`)}
                    labelSubtitle={<Trans i18nKey={`ticket.event.minisite.${modalConfirm}.subtitle`} />}
                    labelBody={
                        <>
                            <div className={Styles['site-url']}>
                                <div className={Styles['title']}>{`https://${formMinisite['domain']}/`}</div>
                                <Form.Field className={Styles['site-url-input']}>
                                    <Input
                                        placeholder={t('ticket.event.minisite.security_options.address')}
                                        value={formMinisite['slug']}
                                        onChange={(ev, { value }) => handleInputChange(value, false)}
                                        errors={errors}
                                    />
                                    <FieldErrors errors={errors} />
                                </Form.Field>
                            </div>
                            <Trans i18nKey={`ticket.event.minisite.${modalConfirm}.content`} />
                        </>
                    }
                    size="large"
                    objectToSave={formMinisite}
                    onClose={() => {
                        resetValue(modalConfirm);
                        setModalConfirm(null);
                    }}
                    isSubmitting={isLoading}
                    onAccept={() => {
                        handleInputChange(formMinisite[modalConfirm]);
                    }}
                    disabled={errors.length > 0}
                />
            )}
        </Form>
    );
}
SecurityOptions.propTypes = {
    organization: PropTypes.object.isRequired,
    formMinisite: PropTypes.object.isRequired,
    initialValues: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired,
    resetValue: PropTypes.func.isRequired
};
